import * as Yup from 'yup';
import 'yup-phone-lite';
import { sellerTypes } from '../../components/credit-application/values';
import { CompanyConfig } from '../../company.config';
import { domainHasMxRecord } from '../../utils/mx-check.util';

Yup.setLocale({
  mixed: {
    required: 'Required Field',
  },
  string: {
    trim: 'Please remove any leading or trailing whitespace',
  },
});
export const requiredTrimmedString = () =>
  Yup.string()
    .strict(false) // This makes trim() transform instead of validate
    .trim()
    .required();

const optionalTrimmedString = () =>
  Yup.string()
    .strict(false) // This makes trim() transform instead of validate
    .trim();

export const validEmail = () =>
  Yup.string()
    .email('Please enter a valid email address')
    .matches(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'Please enter a valid email address with a proper domain'
    )
    .test('mx-record', "This domain can't receive email — please check for typos", async (value) => {
      if (!value || !value.includes('@')) {
        return true;
      }

      const domain = value.split('@')[1];
      if (!domain || !/\.[a-zA-Z]{2,}$/.test(domain)) {
        return true; // domain doesn't look complete yet, skip the check
      }

      return domainHasMxRecord(domain);
    });

// STEP #1
export const ApplicantInformationSchema = (isPhone: boolean) =>
  Yup.object().shape({
    firstName: requiredTrimmedString().min(2).max(50),
    middleName: optionalTrimmedString(),
    lastName: requiredTrimmedString().min(2).max(50),
    suffix: optionalTrimmedString(),
    emailAddress: validEmail().required(),
    maritalStatus: requiredTrimmedString(),
    birthdate: Yup.string()
      .matches(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/, 'Date must be in MM/DD/YYYY format')
      .required('Birth date is required.'),
    primaryPhoneType: requiredTrimmedString(),
    primaryPhoneNumber: Yup.string().phone('US', 'Please enter a valid phone number').required(),
    ssn: Yup.string()
      .matches(/^\d{3}-\d{2}-\d{4}$/, 'Invalid SSN format')
      .required(),
    // Phone application fields
    createdBy: isPhone
      ? Yup.string().required('Team Member is required').min(2, 'Must be at least 2 characters')
      : Yup.string(),

    phoneNumberCallback: isPhone
      ? Yup.string().required('Callback number is required').phone('US', 'Please enter a valid phone number')
      : Yup.string(),
  });

// STEP #2
const addressLessThan2YearsConditionString = Yup.string().when(
  ['yearsAtResidentialAddress', 'monthsAtResidentialAddress'],
  {
    is: (yearsAtResidentialAddress: string, monthsAtResidentialAddress: string) =>
      Number(yearsAtResidentialAddress) * 12 + Number(monthsAtResidentialAddress) < 24,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }
);
const addressLessThan2YearsConditionNumber = Yup.number().when(
  ['yearsAtResidentialAddress', 'monthsAtResidentialAddress'],
  {
    is: (yearsAtResidentialAddress: string, monthsAtResidentialAddress: string) =>
      Number(yearsAtResidentialAddress) * 12 + Number(monthsAtResidentialAddress) < 24,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }
);

export const ResidentialAddressSchema = Yup.object().shape({
  residentialAddressLine1: requiredTrimmedString(),
  residentialAddressLine2: optionalTrimmedString(),
  residentialCity: requiredTrimmedString().max(40, 'City must be less than 40 characters'),
  residentialState: requiredTrimmedString(),
  residentialZipCode: Yup.string()
    .required('Zip code is required')
    .matches(/^\d{5}$/, 'Zip code must be exactly 5 digits'),
  yearsAtResidentialAddress: Yup.number()
    .integer('Years must be a whole number')
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100')
    .required(),
  monthsAtResidentialAddress: Yup.number()
    .integer('Months must be a whole number')
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12')
    .required(),
  statusAtResidentialAddress: requiredTrimmedString(),
  monthlyPayment: requiredTrimmedString(),

  previousAddressLine1: addressLessThan2YearsConditionString,
  previousCity: addressLessThan2YearsConditionString.max(40, 'City must be less than 40 characters'),
  previousState: addressLessThan2YearsConditionString,
  previousZipCode: addressLessThan2YearsConditionString.matches(/^\d{5}$/, 'Zip code must be exactly 5 digits'),
  previousYearsAtAddress: addressLessThan2YearsConditionNumber
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100'),
  previousMonthsAtAddress: addressLessThan2YearsConditionNumber
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12'),
});

// STEP #3
const haveCoApplicantCondition = Yup.string().when('haveCoApplicant', {
  is: (haveCoApplicant: boolean) => haveCoApplicant === true,
  then: (schema) => schema.required(),
  otherwise: (schema) => schema.optional(),
});

const employedLessThan2YearsConditionString = Yup.string().when(
  ['isRetired', 'retirementYears', 'retirementMonths', 'isEmployed', 'employedYears', 'employedMonths'],
  {
    is: (
      isRetired: boolean,
      retirementYears: string,
      retirementMonths: string,
      isEmployed: boolean,
      employedYears: string,
      employedMonths: string
    ) => {
      const _isRetiredLessThanTwoYears = isRetired && Number(retirementYears) * 12 + Number(retirementMonths) < 24;

      const _isRetiredGreaterThanTwoYears = isRetired && Number(retirementYears) * 12 + Number(retirementMonths) >= 24;

      const _isEmployedLessThanTwoYears = isEmployed && Number(employedYears) * 12 + Number(employedMonths) < 24;

      const _isEmployedGreaterThanTwoYears = isEmployed && Number(employedYears) * 12 + Number(employedMonths) >= 24;

      // Show previous employment ONLY if these conditions are met
      const _shouldShowPreviousEmployment =
        // Show cases
        ((_isRetiredLessThanTwoYears && !isEmployed) || // Retired < 2 years AND not employed
          (_isRetiredLessThanTwoYears && _isEmployedLessThanTwoYears) || // Retired < 2 years AND employed < 2 years
          (_isEmployedLessThanTwoYears && !isRetired) || // Employed < 2 years AND not retired
          (_isEmployedLessThanTwoYears && _isRetiredLessThanTwoYears)) && // Employed < 2 years AND retired < 2 years
        // AND none of the hide cases are true
        !(_isRetiredGreaterThanTwoYears || _isEmployedGreaterThanTwoYears); // Hide if either is > 2 years

      return _shouldShowPreviousEmployment;
    },
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }
);
const employedLessThan2YearsConditionNumber = Yup.number().when(
  ['isRetired', 'retirementYears', 'retirementMonths', 'isEmployed', 'employedYears', 'employedMonths'],
  {
    is: (
      isRetired: boolean,
      retirementYears: string,
      retirementMonths: string,
      isEmployed: boolean,
      employedYears: string,
      employedMonths: string
    ) => {
      const _isRetiredLessThanTwoYears = isRetired && Number(retirementYears) * 12 + Number(retirementMonths) < 24;

      const _isRetiredGreaterThanTwoYears = isRetired && Number(retirementYears) * 12 + Number(retirementMonths) >= 24;

      const _isEmployedLessThanTwoYears = isEmployed && Number(employedYears) * 12 + Number(employedMonths) < 24;

      const _isEmployedGreaterThanTwoYears = isEmployed && Number(employedYears) * 12 + Number(employedMonths) >= 24;

      // Show previous employment ONLY if these conditions are met
      const _shouldShowPreviousEmployment =
        // Show cases
        ((_isRetiredLessThanTwoYears && !isEmployed) || // Retired < 2 years AND not employed
          (_isRetiredLessThanTwoYears && _isEmployedLessThanTwoYears) || // Retired < 2 years AND employed < 2 years
          (_isEmployedLessThanTwoYears && !isRetired) || // Employed < 2 years AND not retired
          (_isEmployedLessThanTwoYears && _isRetiredLessThanTwoYears)) && // Employed < 2 years AND retired < 2 years
        // AND none of the hide cases are true
        !(_isRetiredGreaterThanTwoYears || _isEmployedGreaterThanTwoYears); // Hide if either is > 2 years

      return _shouldShowPreviousEmployment;
    },
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }
);

const coapplicantAddressLessThan2YearsConditionString = Yup.string().when(
  ['coapplicantYearsAtResidentialAddress', 'coapplicantMonthsAtResidentialAddress'],
  {
    is: (coapplicantYearsAtResidentialAddress: string, coapplicantMonthsAtResidentialAddress: string) =>
      Number(coapplicantYearsAtResidentialAddress) * 12 + Number(coapplicantMonthsAtResidentialAddress) < 24,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }
);
const coapplicantAddressLessThan2YearsConditionNumber = Yup.number().when(
  ['coapplicantYearsAtResidentialAddress', 'coapplicantMonthsAtResidentialAddress'],
  {
    is: (coapplicantYearsAtResidentialAddress: string, coapplicantMonthsAtResidentialAddress: string) =>
      Number(coapplicantYearsAtResidentialAddress) * 12 + Number(coapplicantMonthsAtResidentialAddress) < 24,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }
);

const coapplicantEmployedLessThan2YearsConditionString = Yup.string().when(
  [
    'coapplicantIsRetired',
    'coapplicantRetirementYears',
    'coapplicantRetirementMonths',
    'coapplicantIsEmployed',
    'coapplicantEmployedYears',
    'coapplicantEmployedMonths',
  ],
  {
    is: (
      coapplicantIsRetired: boolean,
      coapplicantRetirementYears: string,
      coapplicantRetirementMonths: string,
      coapplicantIsEmployed: boolean,
      coapplicantEmployedYears: string,
      coapplicantEmployedMonths: string
    ) => {
      const _isRetiredLessThanTwoYears =
        coapplicantIsRetired && Number(coapplicantRetirementYears) * 12 + Number(coapplicantRetirementMonths) < 24;

      const _isRetiredGreaterThanTwoYears =
        coapplicantIsRetired && Number(coapplicantRetirementYears) * 12 + Number(coapplicantRetirementMonths) >= 24;

      const _isEmployedLessThanTwoYears =
        coapplicantIsEmployed && Number(coapplicantEmployedYears) * 12 + Number(coapplicantEmployedMonths) < 24;

      const _isEmployedGreaterThanTwoYears =
        coapplicantIsEmployed && Number(coapplicantEmployedYears) * 12 + Number(coapplicantEmployedMonths) >= 24;

      // Show previous employment ONLY if these conditions are met
      const _shouldShowPreviousEmployment =
        // Show cases
        ((_isRetiredLessThanTwoYears && !coapplicantIsEmployed) || // Retired < 2 years AND not employed
          (_isRetiredLessThanTwoYears && _isEmployedLessThanTwoYears) || // Retired < 2 years AND employed < 2 years
          (_isEmployedLessThanTwoYears && !coapplicantIsRetired) || // Employed < 2 years AND not retired
          (_isEmployedLessThanTwoYears && _isRetiredLessThanTwoYears)) && // Employed < 2 years AND retired < 2 years
        // AND none of the hide cases are true
        !(_isRetiredGreaterThanTwoYears || _isEmployedGreaterThanTwoYears); // Hide if either is > 2 years

      return _shouldShowPreviousEmployment;
    },
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }
);
const coapplicantEmployedLessThan2YearsConditionNumber = Yup.number().when(
  [
    'coapplicantIsRetired',
    'coapplicantRetirementYears',
    'coapplicantRetirementMonths',
    'coapplicantIsEmployed',
    'coapplicantEmployedYears',
    'coapplicantEmployedMonths',
  ],
  {
    is: (
      coapplicantIsRetired: boolean,
      coapplicantRetirementYears: string,
      coapplicantRetirementMonths: string,
      coapplicantIsEmployed: boolean,
      coapplicantEmployedYears: string,
      coapplicantEmployedMonths: string
    ) => {
      const _isRetiredLessThanTwoYears =
        coapplicantIsRetired && Number(coapplicantRetirementYears) * 12 + Number(coapplicantRetirementMonths) < 24;

      const _isRetiredGreaterThanTwoYears =
        coapplicantIsRetired && Number(coapplicantRetirementYears) * 12 + Number(coapplicantRetirementMonths) >= 24;

      const _isEmployedLessThanTwoYears =
        coapplicantIsEmployed && Number(coapplicantEmployedYears) * 12 + Number(coapplicantEmployedMonths) < 24;

      const _isEmployedGreaterThanTwoYears =
        coapplicantIsEmployed && Number(coapplicantEmployedYears) * 12 + Number(coapplicantEmployedMonths) >= 24;

      // Show previous employment ONLY if these conditions are met
      const _shouldShowPreviousEmployment =
        // Show cases
        ((_isRetiredLessThanTwoYears && !coapplicantIsEmployed) || // Retired < 2 years AND not employed
          (_isRetiredLessThanTwoYears && _isEmployedLessThanTwoYears) || // Retired < 2 years AND employed < 2 years
          (_isEmployedLessThanTwoYears && !coapplicantIsRetired) || // Employed < 2 years AND not retired
          (_isEmployedLessThanTwoYears && _isRetiredLessThanTwoYears)) && // Employed < 2 years AND retired < 2 years
        // AND none of the hide cases are true
        !(_isRetiredGreaterThanTwoYears || _isEmployedGreaterThanTwoYears); // Hide if either is > 2 years

      return _shouldShowPreviousEmployment;
    },
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }
);

export const EmploymentInformationSchema = Yup.object().shape({
  isRetired: Yup.boolean()
    .required('Please select at least one income source')
    .test('at-least-one-selected', 'Please select at least one income source', function () {
      const { isRetired, isEmployed, isAdditionalIncome } = this.parent;
      return isRetired || isEmployed || isAdditionalIncome;
    }),
  isEmployed: Yup.boolean()
    .required('Please select at least one income source')
    .test('at-least-one-selected', 'Please select at least one income source', function () {
      const { isRetired, isEmployed, isAdditionalIncome } = this.parent;
      return isRetired || isEmployed || isAdditionalIncome;
    }),
  isAdditionalIncome: Yup.boolean()
    .required('Please select at least one income source')
    .test('at-least-one-selected', 'Please select at least one income source', function () {
      const { isRetired, isEmployed, isAdditionalIncome } = this.parent;
      return isRetired || isEmployed || isAdditionalIncome;
    }),

  // isRetired: Yup.boolean().optional(),
  retirementIncome: Yup.string().when('isRetired', {
    is: (isRetired: boolean) => isRetired === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  retirementYears: Yup.number()
    .integer('Years must be a whole number')
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100')
    .when('isRetired', {
      is: (isRetired: boolean) => isRetired === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  retirementMonths: Yup.number()
    .integer('Months must be a whole number')
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12')
    .when('isRetired', {
      is: (isRetired: boolean) => isRetired === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),

  // isEmployed: Yup.boolean().optional(),
  employerName: Yup.string()
    .max(100, 'Employer Business Name must be less than 100 characters')
    .when('isEmployed', {
      is: (isEmployed: boolean) => isEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  employerPhoneNumber: Yup.string()
    .phone('US', 'Please enter a valid phone number')
    .when('isEmployed', {
      is: (isEmployed: boolean) => isEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  occupationTitle: Yup.string().when('isEmployed', {
    is: (isEmployed: boolean) => isEmployed === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  grossMonthlyIncome: Yup.string().when('isEmployed', {
    is: (isEmployed: boolean) => isEmployed === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  employerAddressLine1: Yup.string()
    .max(100, 'Employer Address must be less than 100 characters')
    .when('isEmployed', {
      is: (isEmployed: boolean) => isEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  employerCity: Yup.string()
    .when('isEmployed', {
      is: (isEmployed: boolean) => isEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    })
    .max(40, 'City must be less than 40 characters'),
  employerState: Yup.string().when('isEmployed', {
    is: (isEmployed: boolean) => isEmployed === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  employerZipCode: Yup.string()
    .matches(/^\d{5}$/, 'Zip code must be exactly 5 digits')
    .when('isEmployed', {
      is: (isEmployed: boolean) => isEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  employedYears: Yup.number()
    .integer('Years must be a whole number')
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100')
    .when('isEmployed', {
      is: (isEmployed: boolean) => isEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  employedMonths: Yup.number()
    .integer('Months must be a whole number')
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12')
    .when('isEmployed', {
      is: (isEmployed: boolean) => isEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),

  previousEmployerName: employedLessThan2YearsConditionString.max(
    100,
    'Previous Employer Business Name must be less than 100 characters',
  ),
  previousOccupationTitle: employedLessThan2YearsConditionString,
  previousGrossMonthlyIncome: employedLessThan2YearsConditionString,
  previousEmployedYears: employedLessThan2YearsConditionNumber
    .integer('Years must be a whole number')
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100'),
  previousEmployedMonths: employedLessThan2YearsConditionNumber
    .integer('Months must be a whole number')
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12'),

  // isAdditionalIncome: Yup.boolean().optional(),
  additionalIncomeSource: Yup.string().when('isAdditionalIncome', {
    is: (isAdditionalIncome: boolean) => isAdditionalIncome === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  additionalIncomeSourceOther: Yup.string().when('additionalIncomeSource', {
    is: (additionalIncomeSource: string) => additionalIncomeSource && additionalIncomeSource.toLowerCase() === 'other',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  additionalMonthlyIncome: Yup.string().when('additionalIncomeSource', {
    is: (additionalIncomeSource: string) => additionalIncomeSource && additionalIncomeSource.toLowerCase() !== '',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
});

// STEP 4

export const CoapplicantInformationSchema = (applicantSsn?: string, applicantBirthdate?: string) =>
  Yup.object().shape({
    haveCoApplicant: Yup.boolean().required(),
    coapplicantFirstName: haveCoApplicantCondition,
    coapplicantMiddleName: optionalTrimmedString(),
    coapplicantLastName: haveCoApplicantCondition,
    coapplicantSuffix: optionalTrimmedString(),
    coapplicantPrimaryPhoneType: haveCoApplicantCondition,
    coapplicantPrimaryPhoneNumber: haveCoApplicantCondition.phone('US', 'Please enter a valid phone number'),
    coapplicantEmailAddress: Yup.string().when('haveCoApplicant', {
      is: (haveCoApplicant: boolean) => haveCoApplicant === true,
      then: (schema) => validEmail().required(),
      otherwise: (schema) => schema.optional(),
    }),
    coapplicantMaritalStatus: haveCoApplicantCondition,
    coapplicantSsn: haveCoApplicantCondition
      .matches(/^\d{3}-\d{2}-\d{4}$/, 'Invalid SSN format')
      .test(
        'ssn-not-same-as-buyer',
        "Co-buyer SSN must differ from the buyer's SSN",
        (value) => !applicantSsn || !value || value !== applicantSsn
      )
      .required(),
    coapplicantBirthdate: haveCoApplicantCondition
      .matches(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/, 'Date must be in MM/DD/YYYY format')
      .test('different-from-applicant-birthdate', 'Birth date must be different from the buyer', function (value) {
        if (!applicantBirthdate || !value) return true; // Skip validation if either is missing
        return value !== applicantBirthdate;
      }),
  });

// STEP # 5

export const CoapplicantResidentiaAddressSchema = Yup.object().shape({
  coapplicantResidentialAddressLine1: haveCoApplicantCondition,
  coapplicantResidentialCity: haveCoApplicantCondition.max(40, 'City must be less than 40 characters'),
  coapplicantResidentialState: haveCoApplicantCondition,
  coapplicantResidentialZipCode: haveCoApplicantCondition.matches(/^\d{5}$/, 'Zip code must be exactly 5 digits'),
  coapplicantYearsAtResidentialAddress: Yup.number()
    .integer('Years must be a whole number')
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100')
    .when('haveCoApplicant', {
      is: (haveCoApplicant: boolean) => haveCoApplicant === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  coapplicantMonthsAtResidentialAddress: Yup.number()
    .integer('Months must be a whole number')
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12')
    .when('haveCoApplicant', {
      is: (haveCoApplicant: boolean) => haveCoApplicant === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  coapplicantStatusAtResidentialAddress: haveCoApplicantCondition,
  coapplicantMonthlyPayment: haveCoApplicantCondition,

  coapplicantPreviousAddressLine1: coapplicantAddressLessThan2YearsConditionString,
  coapplicantPreviousCity: coapplicantAddressLessThan2YearsConditionString.max(
    40,
    'City must be less than 40 characters'
  ),
  coapplicantPreviousState: coapplicantAddressLessThan2YearsConditionString,
  coapplicantPreviousZipCode: coapplicantAddressLessThan2YearsConditionString.matches(
    /^\d{5}$/,
    'Zip code must be exactly 5 digits'
  ),
  coapplicantPreviousYearsAtAddress: coapplicantAddressLessThan2YearsConditionNumber
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100'),
  coapplicantPreviousMonthsAtAddress: coapplicantAddressLessThan2YearsConditionNumber
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12'),
});

// STEP # 6

export const CoapplicantEmploymentInformationSchema = Yup.object().shape({
  coapplicantIsRetired: Yup.boolean()
    .required('Please select at least one income source')
    .test('at-least-one-selected', 'Please select at least one income source', function () {
      const { coapplicantIsRetired, coapplicantIsEmployed, coapplicantIsAdditionalIncome } = this.parent;
      return coapplicantIsRetired || coapplicantIsEmployed || coapplicantIsAdditionalIncome;
    }),
  coapplicantIsEmployed: Yup.boolean()
    .required('Please select at least one income source')
    .test('at-least-one-selected', 'Please select at least one income source', function () {
      const { coapplicantIsRetired, coapplicantIsEmployed, coapplicantIsAdditionalIncome } = this.parent;
      return coapplicantIsRetired || coapplicantIsEmployed || coapplicantIsAdditionalIncome;
    }),
  coapplicantIsAdditionalIncome: Yup.boolean()
    .required('Please select at least one income source')
    .test('at-least-one-selected', 'Please select at least one income source', function () {
      const { coapplicantIsRetired, coapplicantIsEmployed, coapplicantIsAdditionalIncome } = this.parent;
      return coapplicantIsRetired || coapplicantIsEmployed || coapplicantIsAdditionalIncome;
    }),

  // coapplicantIsRetired: Yup.boolean().optional(),
  coapplicantRetirementIncome: Yup.string().when('coapplicantIsRetired', {
    is: (coapplicantIsRetired: boolean) => coapplicantIsRetired === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  coapplicantRetirementYears: Yup.number()
    .integer('Years must be a whole number')
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100')
    .when('coapplicantIsRetired', {
      is: (coapplicantIsRetired: boolean) => coapplicantIsRetired === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  coapplicantRetirementMonths: Yup.number()
    .integer('Months must be a whole number')
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12')
    .when('coapplicantIsRetired', {
      is: (coapplicantIsRetired: boolean) => coapplicantIsRetired === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),

  coapplicantEmployerName: Yup.string()
    .max(100, 'Employer Business Name must be less than 100 characters')
    .when(['haveCoApplicant', 'coapplicantIsEmployed'], {
      is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
        haveCoApplicant === true && coapplicantIsEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  coapplicantOccupationTitle: Yup.string().when(['haveCoApplicant', 'coapplicantIsEmployed'], {
    is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
      haveCoApplicant === true && coapplicantIsEmployed === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  coapplicantGrossMonthlyIncome: Yup.string().when(['haveCoApplicant', 'coapplicantIsEmployed'], {
    is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
      haveCoApplicant === true && coapplicantIsEmployed === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  coapplicantEmployerAddressLine1: Yup.string()
    .max(100, 'Employer Address must be less than 100 characters')
    .when(['haveCoApplicant', 'coapplicantIsEmployed'], {
      is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
        haveCoApplicant === true && coapplicantIsEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  coapplicantEmployerCity: Yup.string()
    .when(['haveCoApplicant', 'coapplicantIsEmployed'], {
      is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
        haveCoApplicant === true && coapplicantIsEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    })
    .max(40, 'City must be less than 40 characters'),
  coapplicantEmployerState: Yup.string().when(['haveCoApplicant', 'coapplicantIsEmployed'], {
    is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
      haveCoApplicant === true && coapplicantIsEmployed === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  coapplicantEmployerZipCode: Yup.string()
    .matches(/^\d{5}$/, 'Zip code must be exactly 5 digits')
    .when(['haveCoApplicant', 'coapplicantIsEmployed'], {
      is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
        haveCoApplicant === true && coapplicantIsEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  coapplicantEmployerPhoneNumber: Yup.string()
    .phone('US', 'Please enter a valid phone number')
    .when(['haveCoApplicant', 'coapplicantIsEmployed'], {
      is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
        haveCoApplicant === true && coapplicantIsEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  coapplicantEmployedYears: Yup.number()
    .integer('Years must be a whole number')
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100')
    .when(['haveCoApplicant', 'coapplicantIsEmployed'], {
      is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
        haveCoApplicant === true && coapplicantIsEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  coapplicantEmployedMonths: Yup.number()
    .integer('Months must be a whole number')
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12')
    .when(['haveCoApplicant', 'coapplicantIsEmployed'], {
      is: (haveCoApplicant: boolean, coapplicantIsEmployed: boolean) =>
        haveCoApplicant === true && coapplicantIsEmployed === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),

  coapplicantPreviousEmployerName: coapplicantEmployedLessThan2YearsConditionString.max(
    100,
    'Previous Employer Business Name must be less than 100 characters',
  ),
  coapplicantPreviousOccupationTitle: coapplicantEmployedLessThan2YearsConditionString,
  coapplicantPreviousGrossMonthlyIncome: coapplicantEmployedLessThan2YearsConditionString,
  coapplicantPreviousEmployedYears: coapplicantEmployedLessThan2YearsConditionNumber
    .integer('Years must be a whole number')
    .min(0, 'Years must be at least 0')
    .max(99, 'Years must be less than 100'),
  coapplicantPreviousEmployedMonths: coapplicantEmployedLessThan2YearsConditionNumber
    .integer('Months must be a whole number')
    .min(0, 'Months must be at least 0')
    .max(11, 'Months must be less than 12'),

  coapplicantAdditionalIncomeSource: Yup.string().when('coapplicantIsAdditionalIncome', {
    is: (coapplicantIsAdditionalIncome: boolean) => coapplicantIsAdditionalIncome === true,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  coapplicantAdditionalIncomeSourceOther: Yup.string().when('coapplicantAdditionalIncomeSource', {
    is: (coapplicantAdditionalIncomeSource: string) =>
      coapplicantAdditionalIncomeSource && coapplicantAdditionalIncomeSource.toLowerCase() === 'other',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  coapplicantAdditionalMonthlyIncome: Yup.string().when('coapplicantAdditionalIncomeSource', {
    is: (coapplicantAdditionalIncomeSource: string) =>
      coapplicantAdditionalIncomeSource && coapplicantAdditionalIncomeSource.toLowerCase() !== '',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
});

// STEP 7

export const SaleDetailsSchema = Yup.object().shape({
  sellerType: requiredTrimmedString().oneOf(sellerTypes),
  sellerName: optionalTrimmedString(),
  sellerPhoneNumber: Yup.string().phone('US', 'Please enter a valid phone number').optional(),
  sellerContact: optionalTrimmedString(),
  dealerName: optionalTrimmedString(),
  vehicleYear: Yup.number().integer('Year must be a whole number').min(1800).max(2100).required(),
  vehicleMake: requiredTrimmedString(),
  vehicleModel: requiredTrimmedString(),
  // vehicleSalesPrice: Yup.number().min(0).required(),
  // vehicleDownPayment: Yup.number()
  //   .min(0)
  //   .max(Yup.ref("vehicleSalesPrice"), "Down payment cannot be greater than sales price")
  //   .required(),
  vehicleSalesPrice: Yup.string()
    .test('min', 'Amount must be greater than 0', (value) => {
      const numValue = Number(value?.replace(/,/g, ''));
      return numValue > 0;
    })
    .required(),
  vehicleDownPayment: Yup.string()
    .test('min', 'Amount must be greater than 0', (value) => {
      const numValue = Number(value?.replace(/,/g, ''));
      return numValue > 0;
    })
    .test('max', 'Down payment cannot be greater than sales price', function (value) {
      const downPayment = Number(value?.replace(/,/g, ''));
      const salesPrice = Number(this.parent.vehicleSalesPrice?.replace(/,/g, ''));
      return downPayment <= salesPrice;
    })
    .required(),
  notes: Yup.string().optional().max(1000, 'Notes cannot be longer than 1000 characters'),

  referralSource: requiredTrimmedString().oneOf(CompanyConfig.referralSources),
  referralSourceOther: Yup.string().when('referralSource', {
    is: (referralSource?: string) => referralSource?.toLowerCase() === 'other',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
});

// STEP 8
export const ConsentSchema = Yup.object().shape({
  firstName: requiredTrimmedString(),
  lastName: requiredTrimmedString(),

  usCitizen: Yup.boolean()
    .required('Choose only one: US Citizen OR US Resident')
    .test('citizen-or-resident', 'Choose only one: US Citizen OR US Resident', function () {
      const { usCitizen, usResident } = this.parent;
      return (usCitizen && !usResident) || (!usCitizen && usResident);
    }),
  usResident: Yup.boolean().required('Choose only one: US Citizen OR US Resident'),

  applicantAgree: Yup.boolean()
    .required('Please agree to the terms and conditions')
    .oneOf([true], 'Please agree to the terms and conditions'),
  applicantSignature: Yup.string()
    .required()
    .test('matches-name', 'Signature must start with your first name and end with your last name', function (value) {
      const { firstName, lastName } = this.parent;
      const trimmedValue = value.trim().toLowerCase();
      const firstNameLower = firstName.trim().toLowerCase();
      const lastNameLower = lastName.trim().toLowerCase();

      return trimmedValue.startsWith(firstNameLower) && trimmedValue.endsWith(lastNameLower);
    }),

  coapplicantFirstName: optionalTrimmedString(),
  coapplicantLastName: optionalTrimmedString(),

  coApplicantAgree: Yup.boolean().when('haveCoApplicant', {
    is: (haveCoApplicant: boolean) => haveCoApplicant === true,
    then: (schema) =>
      schema
        .required('Please agree to the terms and conditions')
        .oneOf([true], 'Please agree to the terms and conditions'),
    otherwise: (schema) => schema.optional(),
  }),
  coApplicantSignature: Yup.string().when('haveCoApplicant', {
    is: (haveCoApplicant: boolean) => haveCoApplicant === true,
    then: (schema) =>
      schema
        .required()
        .test('matches-name', 'Signature must start with co-buyer first name and end with last name', function (value) {
          const { coapplicantFirstName, coapplicantLastName } = this.parent;
          const trimmedValue = value.trim().toLowerCase();
          const firstNameLower = coapplicantFirstName.trim().toLowerCase();
          const lastNameLower = coapplicantLastName.trim().toLowerCase();

          return trimmedValue.startsWith(firstNameLower) && trimmedValue.endsWith(lastNameLower);
        }),
    otherwise: (schema) => schema.optional(),
  }),

  coApplicantUsCitizen: Yup.boolean().when('haveCoApplicant', {
    is: (haveCoApplicant: boolean) => haveCoApplicant === true,
    then: (schema) =>
      schema
        .required('Choose only one: US Citizen OR US Resident')
        .test('citizen-or-resident', 'Choose only one: US Citizen OR US Resident', function () {
          const { coApplicantUsCitizen, coApplicantUsResident } = this.parent;
          return (coApplicantUsCitizen && !coApplicantUsResident) || (!coApplicantUsCitizen && coApplicantUsResident);
        }),
    otherwise: (schema) => schema.optional(),
  }),
  coApplicantUsResident: Yup.boolean().when('haveCoApplicant', {
    is: (haveCoApplicant: boolean) => haveCoApplicant === true,
    then: (schema) => schema.required('Choose only one: US Citizen OR US Resident'),
    otherwise: (schema) => schema.optional(),
  }),
});

/** Inline (single-page) service form: same rules as the multi-step wizard, with co-buyer employment only when haveCoApplicant is true. */
export function buildInlineServiceFormSchema(isPhone: boolean) {
  return Yup.lazy((values: any) => {
    const applicantSsn = values?.ssn;
    const applicantBirthdate = values?.birthdate;
    const schemas: Yup.ObjectSchema<any>[] = [
      ApplicantInformationSchema(isPhone),
      ResidentialAddressSchema,
      EmploymentInformationSchema,
      CoapplicantInformationSchema(applicantSsn, applicantBirthdate),
      CoapplicantResidentiaAddressSchema,
    ];
    if (values?.haveCoApplicant === true) {
      schemas.push(CoapplicantEmploymentInformationSchema);
    }
    schemas.push(SaleDetailsSchema);
    schemas.push(ConsentSchema);

    return schemas.reduce((acc, sch) => acc.concat(sch), schemas[0]);
  });
}
